import React, { Suspense, useEffect } from "react";
import { useSelector } from "react-redux";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Loader } from "src/components/atoms/Loader";
import { modals, pages, pagesRedirect } from "src/components/routes";
import { SwitchMode, allPages, allowedPages } from "src/navigation";
import { makeHierarchicalPath } from "src/navigation/Link";
import { PAGES } from "src/types/navigation";
import { useRedirect } from "src/navigation/useRedirect";
import { Modal } from "src/components/organisms/Modal";
import modalStyles from "src/components/organisms/Modal/styles.module.css";
import { ParsedModalScheme, useModalsSwitch } from "src/hooks/useModalsSwitch";
import { history } from "src/navigation/store";
import { RootState } from "src/store";
import { getAccessToken, getTgAccessToken } from "src/api";
import { CabinetLayout } from "src/components/templates/CabinetLayout";

export const onClose = () => {
  const { location } = history;
  const { pathname } = location;

  const pathnameMask = new RegExp(`/~/${pathname.split("/~/").pop()}$`);

  history.push({
    ...location,
    search: "",
    pathname: pathname.replace(pathnameMask, "") || "/",
  });
};

const RenderModalContent: React.FC<Omit<ParsedModalScheme, "isOpen">> =
  React.memo(({ scheme, params }) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const Component: React.FC<any> = modals[scheme].component;
    const parsedParams = params ? JSON.parse(params) : {};
    return <Component params={{ ...parsedParams, scheme }} onClose={onClose} />;
  });

export const ModalsSwitch: React.FC<SwitchMode> = ({ mode }) => {
  const schemes = useModalsSwitch({ mode });
  return (
    <>
      {schemes.map((item) => (
        <Modal key={item.scheme} isVisible={item.isOpen} closeModal={onClose} className={modalStyles.mobileBottomSheet}>
          {/* Модалки тоже грузятся по требованию — нужна своя граница загрузки. */}
          <Suspense
            fallback={
              <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
                <Loader />
              </div>
            }
          >
            <RenderModalContent params={item.params} scheme={item.scheme} />
          </Suspense>
        </Modal>
      ))}
    </>
  );
};

const AuthGate: React.FC<SwitchMode & { scheme: PAGES }> = ({
  mode,
  scheme,
}) => {
  const accessToken = getAccessToken();
  const tgAccessToken = getTgAccessToken();
  const hasAuth = accessToken || tgAccessToken;

  const { redirect } = useRedirect();
  const user = useSelector((state: RootState) => state.profile.user);

  useEffect(() => {
    if (hasAuth && user && !user.email) {
      redirect({ scheme: PAGES.EDIT_PROFILE });
    }
  }, [user?.email]);
  if (!allowedPages[mode].includes(scheme)) {
    const url = pagesRedirect[mode];
    const to = makeHierarchicalPath(
      undefined,
      Array.isArray(url) ? url : [url],
      "replace"
    );

    if (to) {
      return <Navigate replace to={to} />;
    }
  }

  const Component = pages[scheme].component;
  return <Component />;
};

const CABINET_PAGES: PAGES[] = [
  PAGES.POSITIONS,
  PAGES.HISTORIES,
  PAGES.EVENTS,
  PAGES.NOTIFICATIONS,
  PAGES.REFERALS,
];

export const PagesSwitch: React.FC<SwitchMode> = ({ mode }) => {
  const location = useLocation();
  const { redirect } = useRedirect();

  useEffect(() => {
    if (location.pathname === "/") {
      redirect({
        scheme: PAGES.ROOT,
      });
    }
    if (location.pathname === "/profile") {
      redirect({
        scheme: PAGES.POSITIONS,
      });
    }
  }, []);

  const cabinetSchemes = allPages.filter((s) => CABINET_PAGES.includes(s));
  const otherSchemes = allPages.filter((s) => !CABINET_PAGES.includes(s));

  // Страницы грузятся по требованию (lazy), поэтому нужна граница Suspense:
  // без неё загрузка любой страницы вне кабинета роняет рендер в ошибку.
  // У кабинета своя граница внутри CabinetLayout — она ближе и сработает первой.
  return (
    <Suspense
      fallback={
        <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
          <Loader />
        </div>
      }
    >
      <Routes
        location={{
          ...location,
          pathname: location.pathname.split("/~/")[0],
        }}
      >
        <Route element={<CabinetLayout />}>
          {cabinetSchemes.map((scheme) => (
            <Route
              key={scheme}
              path={scheme}
              element={<AuthGate mode={mode} scheme={scheme} />}
            />
          ))}
        </Route>
        {otherSchemes.map((scheme) => (
          <Route
            key={scheme}
            path={scheme}
            element={<AuthGate mode={mode} scheme={scheme} />}
          />
        ))}
      </Routes>
    </Suspense>
  );
};
